import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { CssUnit } from '@/types'
import { breakpoints, colors } from '@/styles'
import { toUnit } from '@/utils/styles'
import {
  IncludedFeatures,
  PlanName,
  Price
} from '@/features/Pricing/pricing-plans'
import { PricingButtonLink } from '@/features/Pricing/types'
import { Button } from '@/components/button-new'
import {
  CheckIconBlack,
  EquifaxLogo,
  InfoCircleGrey,
  RemoveIcon
} from '@/components/icons'
import Tooltip from '@/components/popper-tooltip'
import {
  H4,
  ParagraphMedium,
  ParagraphMediumGrey,
  ParagraphRegular,
  ParagraphSmallGrey
} from '@/components/text'

const Card = styled.div<{ top: CssUnit; featured: boolean }>`
  position: relative;
  border-radius: 16px;
  background-color: ${colors.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  width: 100%;
  height: 100%;
  margin-top: ${props => toUnit(props.top) || 0};

  ${props =>
    props.featured &&
    `
    margin-top: 0;
    box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  `}
`

const CardInner = styled.div`
  height: 100%;
  width: 100%;
  padding: 0 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const IconWrapper = styled.div`
  padding-top: 1.5px;
  margin-bottom: 0 !important;
`

const Header = styled.div`
  height: 32px;
  width: 100%;
  background-color: ${colors.blue32};
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 32px;
  color: ${colors.white};
  text-align: center;
`

const CardContent = styled.div`
  max-width: 300px;
  text-align: center;
  margin: 24px 0 32px;
`

const StyledH4 = styled(H4)`
  margin-bottom: 8px;
  color: ${colors.grey08};
`

const PriceWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 16px 0 4px;
`

const AnnualPrice = styled.div`
  margin-left: 4px;
  text-align: left;
`

const MonthlyPrice = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
`

const StyledParagraphSmall = styled(ParagraphSmallGrey)`
  display: flex;
  align-items: center;
  strong {
    font-size: 16px;
    line-height: 20px;
    color: ${colors.grey48};
    margin-right: 4px;
  }
`

const ButtonContainer = styled.div`
  margin-bottom: 32px;
  width: 100%;
  > a,
  > button {
    width: 100%;
  }
`

const FeaturesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  gap: 16px;
`

const FeaturesHeading = styled(ParagraphRegular)`
  line-height: 20px;
  font-weight: 700;
`

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  svg {
    margin-right: 12px;
    flex-shrink: 0;
  }
`

const PriceValueWrapper = styled.div`
  display: flex;
  align-items: center;
`

const PriceValue = styled.p`
  color: ${colors.black};
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  margin: 0;
`

const DollarSign = styled(PriceValue)`
  font-size: 40px;
  line-height: 48px;
  margin-right: 4px;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const EquifaxWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 245px;
  color: ${colors.grey16};
  font-size: 11px;

  svg {
    margin-left: 8px;
    margin-bottom: -5px;
    width: 70px;
  }
`

const PlanDescription = styled(ParagraphMediumGrey)`
  @media only screen and (max-width: 1160px) {
    height: 60px;
  }
  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    height: unset;
  }
`

type PricingFeaturesProps = {
  features: IncludedFeatures
  value: PlanName
  noHeading?: boolean
  removeIcon?: boolean
}

export const PricingFeatures = ({
  features,
  value,
  noHeading,
  removeIcon
}: PricingFeaturesProps) => {
  const { t } = useTranslation('pricing')
  const IconComponent = removeIcon ? RemoveIcon : CheckIconBlack
  return (
    <FeaturesWrapper>
      {!!features.heading && !noHeading && (
        <FeaturesHeading>{features.heading}</FeaturesHeading>
      )}
      {features.included.map(feature => (
        <Feature key={`${value}-${feature.name}`}>
          <IconWrapper>
            <IconComponent width={12} height={12} />
          </IconWrapper>
          <Column>
            <ParagraphMedium>
              {feature.name}&nbsp;&nbsp;
              <Tooltip
                title={feature.tooltipHeading ?? feature.name}
                content={feature.tooltipContent}
                isInlineBlock={true}
              >
                <InfoCircleGrey width={17} height={17} />
              </Tooltip>
            </ParagraphMedium>
            {feature.showPoweredBy && (
              <EquifaxWrapper>
                {t('pricing:Powered by')}
                <EquifaxLogo />
              </EquifaxWrapper>
            )}
          </Column>
        </Feature>
      ))}
    </FeaturesWrapper>
  )
}

type PricingCardProps = {
  name: React.ReactNode
  value: PlanName
  description: React.ReactNode
  price: Price
  features: IncludedFeatures
  buttonText?: string | React.ReactNode
  hideButtons?: boolean
  trackingId: string
  featuredText?: string
  top: number
  disableButton?: boolean
  href?: PricingButtonLink
  onClick?: () => void
}

export const PricingCard = ({
  name,
  value,
  description,
  price,
  features,
  buttonText,
  hideButtons,
  trackingId,
  featuredText,
  top,
  disableButton,
  href,
  onClick
}: PricingCardProps) => {
  const { t } = useTranslation()
  const isFree = price.annual === '0'
  const isFeatured = !!featuredText

  return (
    <Card featured={isFeatured} top={top}>
      {isFeatured && <Header>{featuredText}</Header>}
      <CardInner>
        <CardContent>
          <StyledH4>{name}</StyledH4>
          <PlanDescription>{description}</PlanDescription>
          <PriceWrapper>
            <PriceValueWrapper>
              <DollarSign>$</DollarSign>
              <PriceValue>{price.annual}</PriceValue>
            </PriceValueWrapper>
            {!isFree && (
              <AnnualPrice>
                <ParagraphMediumGrey>
                  {t('pricing:per month')}
                </ParagraphMediumGrey>
                <ParagraphMediumGrey>
                  {t('pricing:billed annually')}
                </ParagraphMediumGrey>
              </AnnualPrice>
            )}
          </PriceWrapper>
          <MonthlyPrice>
            {isFree ? (
              <ParagraphSmallGrey>{t('pricing:forever')}</ParagraphSmallGrey>
            ) : (
              <StyledParagraphSmall>
                <Trans
                  i18nKey="pricing:amount billed monthly"
                  defaults="<0>${{monthlyPrice}}</0> billed monthly"
                  values={{ monthlyPrice: price.monthly }}
                  components={[<strong key={`price-${name}`}></strong>]}
                />
              </StyledParagraphSmall>
            )}
          </MonthlyPrice>
        </CardContent>
        {!hideButtons && (
          <ButtonContainer>
            {onClick ? (
              <Button
                id={trackingId}
                theme={isFeatured ? 'primary' : 'secondary'}
                disabled={disableButton}
                onClick={onClick}
              >
                {buttonText}
              </Button>
            ) : (
              <Button
                href={href}
                id={trackingId}
                theme={isFeatured ? 'primary' : 'secondary'}
                disabled={disableButton}
              >
                {buttonText}
              </Button>
            )}
          </ButtonContainer>
        )}
        <PricingFeatures features={features} value={value} />
      </CardInner>
    </Card>
  )
}

export default PricingCard
